import React from 'react';
import { CardActionsMenu, StatusPill } from '@/shared/components/ui';
import { PrivacyText } from '@/shared/components/ui/PrivacyText';
import { formatPhoneNumber } from '@/shared/utils';
import { COOLDOWN_COLORS, resolveConversationCooldown } from '../../utils/cooldown';
import {
  AssigneeAvatar,
  CardSeparator,
  ChannelMeta,
  ContactAvatar,
  ConversationCardShell,
  CooldownTrack,
  RelativeTime,
  UnreadBadge,
} from './parts';
import type { ConversationCardFacts, ConversationCardViewProps } from './types';

/** Rótulo do papel no rodapé. Três estados, nenhum deles "Atendendo" sem dono. */
const assigneeLabel = (facts: ConversationCardFacts): string => {
  if (!facts.assigneeName) return 'Sem responsável';
  return facts.status === 'closed' ? 'Atendeu' : 'Atendendo';
};

/**
 * Atendimento 01 — "Cabeçalho de pessoa" · papel NORMAL.
 *
 * Mockup aprovado (versão corrigida): comparacao.html:1616–1649 · CSS :598–632.
 */
export const ConversationCardNormal: React.FC<ConversationCardViewProps> = ({
  facts,
  actions,
  statusTag,
  onCardClick,
}) => {
  // Vez do cliente = nós falamos por último: prévia esmaecida e com locutor.
  const weSpokeLast = !facts.waitingOnUs;
  // Barra de esfriamento em TODAS as densidades (decisão do dono, 16/08): o
  // tempo sem cor não se identifica — o mecanismo do card 06 vale aqui também.
  const cooldown = resolveConversationCooldown(facts.lastMessageAt, {
    waitingOnUs: facts.waitingOnUs,
    ...(facts.sla ? { sla: facts.sla } : {}),
  });
  const cooldownColors = COOLDOWN_COLORS[cooldown.level];

  return (
    <ConversationCardShell conversationId={facts.id} density="normal" gap={12} onClick={onCardClick}>
      <div className="flex items-start gap-2 min-w-0">
        <span className="relative flex-none">
          <ContactAvatar name={facts.contactName} picture={facts.contactPicture} size={28} />
          <UnreadBadge
            count={facts.unreadCount}
            variant="floating"
            data-testid={`attendance-card-unread-${facts.id}`}
          />
        </span>

        <div className="flex-1 min-w-0">
          <p
            className="truncate"
            style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.35, color: 'var(--theme-text)' }}
          >
            {facts.contactName}
          </p>
          <PrivacyText
            as="p"
            className="truncate"
            style={{ marginTop: 2, fontSize: 12, color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}
          >
            {facts.contactPhone ? formatPhoneNumber(facts.contactPhone) : 'Sem telefone'}
          </PrivacyText>
        </div>

        {statusTag && (
          <StatusPill
            variant={statusTag.variant}
            label={statusTag.label}
            data-testid={`attendance-card-status-${facts.id}`}
          />
        )}
        {actions.length > 0 && (
          <CardActionsMenu size="sm" actions={actions} data-testid={`attendance-card-menu-${facts.id}`} />
        )}
      </div>

      <p
        className="min-w-0"
        data-testid={`attendance-card-preview-${facts.id}`}
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          fontSize: 13,
          lineHeight: 1.5,
          color: weSpokeLast ? 'var(--theme-muted-text)' : 'var(--theme-card-text)',
        }}
      >
        {weSpokeLast && (
          <span style={{ fontWeight: 600, color: 'var(--theme-text)' }}>Você: </span>
        )}
        {facts.lastMessage || 'Sem mensagens'}
      </p>

      <div
        className="flex items-center min-w-0"
        data-testid={`attendance-card-cooldown-${facts.id}`}
        data-cooldown-level={cooldown.level}
        data-cooldown-source={cooldown.source}
      >
        <CooldownTrack ratio={cooldown.ratio} color={cooldownColors.solid} />
      </div>

      <CardSeparator />

      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-3 min-w-0 overflow-hidden">
          <ChannelMeta providerId={facts.providerId} label={facts.channelLabel} />
          <RelativeTime iso={facts.lastMessageAt} color={cooldownColors.fg} />
        </div>
        <div className="flex items-center flex-none" style={{ gap: 6 }}>
          <span style={{ fontSize: 11, lineHeight: 1, color: 'var(--theme-muted-text)' }}>
            {assigneeLabel(facts)}
          </span>
          <AssigneeAvatar name={facts.assigneeName} picture={facts.assigneePicture} size={20} />
        </div>
      </div>
    </ConversationCardShell>
  );
};
